import {Box} from '@mui/system';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {TrackEventUBA} from '@jobseeker/common-core/utils';

const Navigation = () => {
  // const isLoggedIn = useSelector((state: AppState) => state.auth.isLoggedIn);
  const {competitionIdentity, testSlug} = useSelector(
    (state: AppState) => state.user?.profile
  );
  const registeredForCompetition = competitionIdentity && testSlug;
  const theme = useWrappedTheme();
  const navItems = [
    {
      label: 'About Competition',
      href: '#about-competition',
      actionSrc: 'About Competition',
    },
    // {label: 'Partner Companies', href: '#partner-companies'},
    {
      label: 'Rounds & Timelines',
      href: '#rounds-timelines',
      actionSrc: 'Round Details',
    },
    {label: 'Eligibility', href: '#eligibility', actionSrc: 'Eligibility'},
    {label: 'Contact', href: '#contact', actionSrc: 'Contact'},
    {label: "FAQ's", href: '#faqs', actionSrc: 'FAQ'},
    {label: 'About', href: '#about-iimjobs', actionSrc: 'About'},
  ];

  const [activeItem, setActiveItem] = useState(navItems[0].href);
  const [isSticky, setIsSticky] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);

  const navRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const lastTrackedSectionRef = useRef<string>('');

  useEffect(() => {
    const competitionSection = document.getElementById('competition-banner');
    const aboutSection = document.getElementById('about-competition');
    const rewardSection = document.getElementById('reward-section');

    if (!competitionSection || !aboutSection || !rewardSection) return;

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const isAnySectionIntersecting = entries.some(
          entry => entry.isIntersecting
        );
        setIsSticky(!isAnySectionIntersecting);
      },
      {threshold: 0}
    );

    observer.observe(competitionSection);
    observer.observe(rewardSection);

    return () => {
      observer.unobserve(competitionSection);
      observer.unobserve(rewardSection);
    };
  }, []);

  const scrollToTargetAdjusted = (elementId: string, offset: number = 0) => {
    const element = document.getElementById(elementId);
    if (!element) return;

    const headerOffset = offset;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  const handleNavItemClick = (href: string, offset = 100) => {
    setActiveItem(href);
    scrollToTargetAdjusted(href.slice(1), offset);
  };

  useEffect(() => {
    if (registeredForCompetition && navRef.current) {
      const handleRoundTimelineSection = () => {
        setActiveItem('#rounds-timelines');
        setIsSticky(true);
        handleNavItemClick('#rounds-timelines', 150);
      };
      // let isMounted = true;
      // requestAnimationFrame(() => {
      //   if (isMounted) handleRoundTimelineSection();
      // });
      // return () => {
      //   isMounted = false;
      // };
      const timer = setTimeout(() => {
        handleRoundTimelineSection();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [registeredForCompetition]);

  useEffect(() => {
    if (navRef.current) {
      navRef.current.style.maxWidth = `${navRef.current.offsetWidth}px`;
    }
    if (headerRef.current) {
      const headerHeight = headerRef.current.getBoundingClientRect().height;
      setHeaderOffset(headerHeight);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + headerOffset;
      let currentSection = '';
      navItems.forEach(item => {
        const sectionElement = document.getElementById(item.href.slice(1));
        if (sectionElement) {
          const sectionTop = sectionElement.offsetTop - 200;
          if (scrollPosition >= sectionTop) {
            currentSection = item.href;
          }
        }
      });
      if (navRef.current) {
        setActiveItem(currentSection);
        setIsSticky(window.scrollY > (navRef.current?.offsetTop || 0));

        if (
          currentSection &&
          currentSection !== lastTrackedSectionRef.current
        ) {
          lastTrackedSectionRef.current = currentSection;
          const section = navItems.find(item => item.href === currentSection);
          if (section) {
            TrackEventUBA('marketingPageView', 'view', {
              pageName: 'EPOCH Landing',
              initiativeName: `EPOCH${new Date().getFullYear()}`,
              actionSrc: section.actionSrc,
            });
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headerOffset, navItems]);

  return (
    <Box ref={headerRef}>
      <Box
        sx={{
          margin: '0 auto',
          marginTop: 4,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          component="nav"
          ref={navRef}
          sx={{
            width: '100%',
            maxWidth: '100%',
            height: '56px',
            padding: 2,
            borderRadius: 2.5,
            border: 'solid 1px #ededf4',
            backgroundColor: '#fff',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: {xs: 5, md: 12.5},
            overflowX: 'auto',
            overflowY: 'hidden',
            flexDirection: 'column',
            paddingBottom: 0,
            position: isSticky ? 'fixed' : 'static',
            top: isSticky ? headerOffset - 5 : 0,
            left: 0,
            right: 0,
            margin: '0 auto',
            zIndex: 1100,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            transition: 'top 0.3s ease',
          }}
        >
          {navItems.map((item, index) => (
            <Box
              key={index}
              component="span"
              sx={{
                fontSize: theme.typography.pxToRem(15),
                fontWeight: 600,
                color: activeItem === item.href ? 'primary.main' : '#2e2e2e',
                cursor: 'pointer',
                borderBottom: activeItem === item.href ? '3px solid' : 'none',
                borderColor: activeItem === item.href ? 'primary.main' : '',
                paddingBottom: 1.5,
                '&:hover': {
                  color: 'primary.main',
                  borderBottom: '3px solid primary.main',
                },
              }}
              onClick={() => handleNavItemClick(item.href)}
            >
              {item.label}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Navigation;
